import Phaser from 'phaser';
import type { PetDef } from '@/domain/types/pet';
import type { Variant } from '@/domain/types/common';
import { PetAnimator } from '@/game/fx/PetAnimator';
import {
  variantScaleMult,
  applyVariantFx,
  destroyVariantFx,
  type VariantFxRefs,
} from '@/game/fx/variantFx';

const SPRITE_DISPLAY_SIZE = 38;
const FOLLOW_LERP = 0.12;

export interface PlazaPetOwner {
  readonly x: number;
  readonly y: number;
}

export interface PlazaFollowPetOpts {
  uid: string;
  def: PetDef;
  variant: Variant;
  slotIndex: number;
  slotCount: number;
  owner: PlazaPetOwner;
}

/**
 * Lekki pet-follow dla PlazaScene.
 *
 * Cel: pokazać drużynę swoją i innych graczy w plazie bez kosztów pełnego
 * gameplayowego `Pet` (targetowanie, atak, damage, sparkle emittery). Pozycja
 * jest odtwarzana lokalnie z owner.x/y, nie sieciujemy ruchu petów.
 */
export class PlazaFollowPet {
  readonly uid: string;
  readonly sprite: Phaser.GameObjects.Image;
  readonly animator: PetAnimator;

  private readonly owner: PlazaPetOwner;
  private readonly slotIndex: number;
  private readonly slotCount: number;
  private logicalX: number;
  private logicalY: number;
  private prevLogicalX: number;
  private prevLogicalY: number;
  private variantFxRefs: VariantFxRefs | null = null;
  private culled = false;

  rainbowHue = 0;
  goldShimmerProgress = 0;

  constructor(scene: Phaser.Scene, opts: PlazaFollowPetOpts) {
    this.uid = opts.uid;
    this.owner = opts.owner;
    this.slotIndex = opts.slotIndex;
    this.slotCount = Math.max(1, opts.slotCount);
    this.logicalX = opts.owner.x;
    this.logicalY = opts.owner.y;
    this.prevLogicalX = this.logicalX;
    this.prevLogicalY = this.logicalY;

    const tex = scene.textures.exists(opts.def.art.textureKey)
      ? opts.def.art.textureKey
      : 'placeholder-square';
    this.sprite = scene.add.image(this.logicalX, this.logicalY, tex);
    const sizeMult = (opts.def.art.scale ?? 1) * variantScaleMult(opts.variant);
    const baseScale = (SPRITE_DISPLAY_SIZE / Math.max(this.sprite.width, this.sprite.height)) * sizeMult;
    this.sprite.setScale(baseScale);
    this.sprite.setDepth(this.logicalY - 2);

    this.animator = new PetAnimator(scene, this.sprite, baseScale, this.logicalX, this.logicalY);
    this.variantFxRefs = applyVariantFx(
      scene,
      this.sprite,
      opts.variant,
      this,
      { x: this.logicalX, y: this.logicalY },
    );
  }

  update(dt: number, now: number): void {
    const slot = this.slotPosition(now);
    this.logicalX += (slot.x - this.logicalX) * FOLLOW_LERP;
    this.logicalY += (slot.y - this.logicalY) * FOLLOW_LERP;

    const vx = dt > 0 ? (this.logicalX - this.prevLogicalX) / dt : 0;
    const vy = dt > 0 ? (this.logicalY - this.prevLogicalY) / dt : 0;
    this.prevLogicalX = this.logicalX;
    this.prevLogicalY = this.logicalY;

    const { offsetX, offsetY } = this.animator.update(
      now,
      dt,
      vx,
      vy,
      this.logicalX,
      this.logicalY,
    );

    this.sprite.x = this.logicalX + offsetX;
    this.sprite.y = this.logicalY + offsetY;
    this.sprite.setDepth(this.sprite.y - 2);

    const aura = this.variantFxRefs?.aura;
    if (aura) {
      aura.x = this.sprite.x;
      aura.y = this.sprite.y;
      aura.setDepth(this.sprite.depth - 1);
    }
  }

  setCulled(culled: boolean): void {
    if (this.culled === culled) return;
    this.culled = culled;
    const visible = !culled;
    this.sprite.setVisible(visible);
    this.animator.shadow.setVisible(visible);
    if (this.variantFxRefs) {
      this.variantFxRefs.aura?.setVisible(visible);
      for (const tween of this.variantFxRefs.tweens) {
        if (culled) tween.pause();
        else tween.resume();
      }
    }
  }

  destroy(): void {
    if (this.variantFxRefs) {
      destroyVariantFx(this.variantFxRefs);
      this.variantFxRefs = null;
    }
    this.animator.destroy();
    this.sprite.destroy();
  }

  private slotPosition(now: number): { x: number; y: number } {
    const radius = 48 + Math.min(this.slotCount, 6) * 6;
    const angle = (this.slotIndex / this.slotCount) * Math.PI * 2 + now * 0.00025;
    return {
      x: this.owner.x + Math.cos(angle) * radius,
      y: this.owner.y + Math.sin(angle) * radius + 18,
    };
  }
}
